import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  FiChevronLeft,
  FiChevronRight,
  FiSearch,
  FiRefreshCw,
  FiBriefcase,
  FiAlertCircle,
  FiClock,
  FiUsers,
  FiCheckCircle,
  FiPlus,
  FiX,
} from 'react-icons/fi';
import { toast } from 'react-toastify';
import { graphqlRequest } from '../../../utils/graphqlRequest';
import '../styles/RostersAdmin.css';
import RosterTaskEditor from './RosterTaskEditor';

const GET_ADMIN_ROSTER_TASKS = `
  query GetAdminRosterTasks {
    getAdminRosterTasks {
      id
      title
      location
      date
      time
      status
      updatedAt
      user { id firstName lastName }
      assignees { id firstName lastName }
      team { id name }
      project {
        id
        name
        projectId
        startDate
        startTime
        endTime
        booking { id fullName address }
        manager { id firstName lastName }
      }
    }
  }
`;

const GET_PROJECTS = `
  query GetRosterProjects($limit: Int) {
    findAllProject(limit: $limit) {
      id
      projectId
      name
      status
      startDate
      startTime
      endTime
      booking { id fullName address }
      manager { id firstName lastName }
    }
  }
`;

const GET_TASKS_BY_PROJECT = `
  query GetRosterTasks($projectId: String!) {
    findTasksByProject(projectId: $projectId) {
      id
      title
      location
      date
      time
      status
      user { id firstName lastName }
      assignees { id firstName lastName }
      team { id name }
    }
  }
`;

const GET_CONTRACTOR_ASSIGNMENTS = `
  query GetAllContractorAssignments {
    getAllContractorAssignments {
      id
      status
      contractorName
      price
      createdAt
      contractor { id firstName lastName }
      assignedBy { id firstName lastName }
      project {
        id
        name
        projectId
        startDate
        startTime
        endTime
        booking { id fullName address }
        manager { id firstName lastName }
      }
    }
  }
`;

const ACCENT = {
  cleaner: '#2563eb',
  contractor: '#f97316',
  unassigned: '#ef4444',
  pending: '#a855f7',
};

const toYmd = (d) => {
  if (!(d instanceof Date) || Number.isNaN(d.getTime())) return null;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const parseTaskDate = (raw) => {
  if (!raw) return null;
  const s = String(raw).trim();
  // Prefer calendar Y-M-D from the string so UTC timestamps don't shift the day.
  const ymd = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (ymd) {
    const y = Number(ymd[1]);
    const m = Number(ymd[2]);
    const d = Number(ymd[3]);
    return new Date(y, m - 1, d);
  }
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return null;
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
};

const startOfWeekMonday = (d) => {
  const x = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const day = x.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  x.setDate(x.getDate() + diff);
  x.setHours(0, 0, 0, 0);
  return x;
};

const addDays = (d, n) => {
  const x = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  x.setDate(x.getDate() + n);
  return x;
};

const fullName = (u) => `${u?.firstName || ''} ${u?.lastName || ''}`.trim();

const suburbFromAddress = (address, location) => {
  const raw = String(address || location || '').trim();
  if (!raw) return '';
  const parts = raw
    .split(',')
    .map((p) => p.trim())
    .filter(Boolean);
  if (parts.length >= 2) return parts[parts.length - 2] || parts[0];
  const m = raw.match(/\b([A-Za-z][A-Za-z\s'-]{2,})\s+\d{4}\b/);
  if (m) return m[1].trim();
  return parts[0] || '';
};

const parseHoursFromTime = (time, startTime, endTime) => {
  const range = String(time || '').trim();
  const m = range.match(/(\d{1,2}):(\d{2})\s*[-–]\s*(\d{1,2}):(\d{2})/);
  if (m) {
    const a = Number(m[1]) * 60 + Number(m[2]);
    const b = Number(m[3]) * 60 + Number(m[4]);
    const mins = b >= a ? b - a : b + 24 * 60 - a;
    return mins / 60;
  }
  const st = String(startTime || '').match(/(\d{1,2}):(\d{2})/);
  const et = String(endTime || '').match(/(\d{1,2}):(\d{2})/);
  if (st && et) {
    const a = Number(st[1]) * 60 + Number(st[2]);
    const b = Number(et[1]) * 60 + Number(et[2]);
    const mins = b >= a ? b - a : b + 24 * 60 - a;
    return mins / 60;
  }
  if (/^\d{1,2}:\d{2}/.test(range)) return 2;
  return 0;
};

const formatTimeLabel = (time, startTime, endTime) => {
  const range = String(time || '').trim();
  if (range && /[-–]/.test(range)) return range.replace(/\s*-\s*/g, '–');
  const st = String(startTime || '').trim();
  const et = String(endTime || '').trim();
  if (st && et) return `${st}–${et}`;
  if (range) return range;
  if (st) return st;
  return 'Time TBD';
};

const isCancelled = (status) => {
  const s = String(status || '').toLowerCase();
  return s === 'cancel' || s === 'cancelled';
};

async function mapInChunks(items, chunkSize, fn) {
  const out = [];
  for (let i = 0; i < items.length; i += chunkSize) {
    const chunk = items.slice(i, i + chunkSize);
    // eslint-disable-next-line no-await-in-loop
    const part = await Promise.all(chunk.map(fn));
    out.push(...part);
  }
  return out;
}

/** Resolve a board date: task date → project start → assignment day. */
function resolveShiftDate(taskDate, projectStart, updatedAt) {
  const primary = parseTaskDate(taskDate);
  if (primary) {
    return { dateObj: primary, dateRaw: taskDate, dateSource: 'task' };
  }
  const fromProject = parseTaskDate(projectStart);
  if (fromProject) {
    return {
      dateObj: fromProject,
      dateRaw: projectStart,
      dateSource: 'project',
    };
  }
  const fromUpdated = parseTaskDate(updatedAt);
  if (fromUpdated) {
    return {
      dateObj: fromUpdated,
      dateRaw: updatedAt,
      dateSource: 'assigned',
    };
  }
  return { dateObj: null, dateRaw: null, dateSource: null };
}

function peopleOnTask(t) {
  const seen = new Set();
  const people = [];
  for (const u of [...(t?.assignees || []), t?.user]) {
    if (!u?.id || seen.has(u.id)) continue;
    seen.add(u.id);
    people.push(u);
  }
  return people;
}

/** Map SAS task (manager Task Rostering) → weekly roster card. */
function taskToShift(t, projectFallback = null, workerOverride = null) {
  const p = t.project || projectFallback || {};
  const workerUser = workerOverride || t.user;
  const worker = fullName(workerUser) || t.team?.name || '';
  const unassigned = !worker;
  const { dateRaw, dateObj, dateSource } = resolveShiftDate(
    t.date,
    p.startDate,
    t.updatedAt,
  );
  const status = String(t.status || '').toLowerCase();
  const hours = parseHoursFromTime(t.time, p.startTime, p.endTime);
  return {
    id: `task-${t.id}${workerUser?.id ? `-${workerUser.id}` : ''}`,
    source: 'task',
    kind: 'cleaner',
    roleLabel: unassigned ? 'UNASSIGNED' : 'Cleaner',
    manager: p.manager ? fullName(p.manager) : 'Unassigned manager',
    managerId: p.manager?.id || '',
    client: p.booking?.fullName || '—',
    projectName: p.name || 'Untitled project',
    projectCode: p.projectId || '',
    taskTitle: t.title || 'Task',
    suburb: suburbFromAddress(p.booking?.address, t.location),
    address: p.booking?.address || t.location || '',
    worker: worker || 'Unassigned',
    workerId: workerUser?.id || t.team?.id || '',
    unassigned,
    dateRaw,
    dateObj,
    dateSource,
    timeLabel: formatTimeLabel(t.time, p.startTime, p.endTime),
    hours,
    status,
    accent: unassigned
      ? ACCENT.unassigned
      : status === 'pending'
        ? ACCENT.pending
        : ACCENT.cleaner,
  };
}

function taskToShifts(t, projectFallback = null) {
  const people = peopleOnTask(t);
  if (!people.length) return [taskToShift(t, projectFallback)];
  return people.map((u) => taskToShift(t, projectFallback, u));
}
function contractorToShift(c) {
  const p = c.project || {};
  const worker =
    fullName(c.contractor) || c.contractorName || 'Contractor';
  const { dateRaw, dateObj, dateSource } = resolveShiftDate(
    p.startDate,
    null,
    c.createdAt,
  );
  const hours = parseHoursFromTime('', p.startTime, p.endTime) || 0;
  return {
    id: `contractor-${c.id}`,
    source: 'contractor',
    kind: 'contractor',
    roleLabel: 'Contractor',
    manager: fullName(p.manager) || fullName(c.assignedBy) || 'Unassigned manager',
    managerId: p.manager?.id || c.assignedBy?.id || '',
    client: p.booking?.fullName || '—',
    projectName: p.name || 'Untitled project',
    projectCode: p.projectId || '',
    taskTitle: 'Contractor assignment',
    suburb: suburbFromAddress(p.booking?.address, ''),
    address: p.booking?.address || '',
    worker,
    workerId: c.contractor?.id || c.contractorName || '',
    unassigned: false,
    dateRaw,
    dateObj,
    dateSource,
    timeLabel: formatTimeLabel('', p.startTime, p.endTime),
    hours,
    status: String(c.status || '').toLowerCase(),
    accent: ACCENT.contractor,
  };
}

export default function RostersAdmin() {
  const [loading, setLoading] = useState(true);
  const [shifts, setShifts] = useState([]);
  const [err, setErr] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const [weekPinned, setWeekPinned] = useState(false);

  const [weekStart, setWeekStart] = useState(() =>
    startOfWeekMonday(new Date()),
  );
  const [search, setSearch] = useState('');
  const [workerFilter, setWorkerFilter] = useState('all');
  const [managerFilter, setManagerFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all'); // all | cleaner | contractor | unassigned
  const [createOpen, setCreateOpen] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [createProjects, setCreateProjects] = useState([]);
  const [createSearch, setCreateSearch] = useState('');
  const [selectedCreateProject, setSelectedCreateProject] = useState(null);
  const [editorProjectId, setEditorProjectId] = useState(null);

  const loadProjectsFallback = useCallback(async () => {
    const projectRes = await graphqlRequest(GET_PROJECTS, { limit: 300 });
    const projects = Array.isArray(projectRes?.findAllProject)
      ? projectRes.findAllProject
      : [];
    const nested = await mapInChunks(projects, 10, async (p) => {
      try {
        const res = await graphqlRequest(GET_TASKS_BY_PROJECT, {
          projectId: p.id,
        });
        const tasks = Array.isArray(res?.findTasksByProject)
          ? res.findTasksByProject
          : [];
        return tasks
          .filter((t) => !isCancelled(t?.status))
          .flatMap((t) => taskToShifts(t, p));
      } catch {
        return [];
      }
    });
    return nested.flat();
  }, []);

  const load = useCallback(async (isRefresh = false) => {
    try {
      if (isRefresh) setRefreshing(true);
      else setLoading(true);
      setErr('');

      let taskShifts = [];
      let usedFallback = false;
      try {
        const taskRes = await graphqlRequest(GET_ADMIN_ROSTER_TASKS);
        const tasks = Array.isArray(taskRes?.getAdminRosterTasks)
          ? taskRes.getAdminRosterTasks
          : [];
        taskShifts = tasks
          .filter((t) => !isCancelled(t?.status))
          .flatMap((t) => taskToShifts(t));
      } catch (primaryErr) {
        console.warn(
          'getAdminRosterTasks unavailable, falling back:',
          primaryErr?.message || primaryErr,
        );
        usedFallback = true;
        if (!isRefresh) {
          taskShifts = await loadProjectsFallback();
        }
      }

      // Primary can succeed with [] when company scope mismatches — still try project walk.
      if (!usedFallback && taskShifts.length === 0 && !isRefresh) {
        try {
          const fallback = await loadProjectsFallback();
          if (fallback.length) taskShifts = fallback;
        } catch (fallbackErr) {
          console.warn(
            'Roster project fallback failed:',
            fallbackErr?.message || fallbackErr,
          );
        }
      }

      let contractors = [];
      try {
        const contractorRes = await graphqlRequest(GET_CONTRACTOR_ASSIGNMENTS);
        contractors = Array.isArray(contractorRes?.getAllContractorAssignments)
          ? contractorRes.getAllContractorAssignments
          : [];
      } catch (e) {
        console.warn('Contractor assignments unavailable', e?.message || e);
      }

      const contractorShifts = contractors
        .filter((c) => String(c.status || '').toLowerCase() !== 'declined')
        .map(contractorToShift);

      setShifts([...taskShifts, ...contractorShifts]);
      if (isRefresh) setWeekPinned(false);
    } catch (e) {
      console.error(e);
      const msg = e?.message || 'Failed to load rosters';
      setErr(msg);
      toast.error(msg);
      setShifts([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [loadProjectsFallback]);

  useEffect(() => {
    void load();
  }, [load]);

  // If this week is empty but assignments exist, jump to the nearest week with work.
  useEffect(() => {
    if (loading || weekPinned) return;
    const dated = shifts.filter((s) => s.dateObj);
    if (!dated.length) return;
    const weekEndLocal = addDays(weekStart, 6);
    const inWeek = dated.some(
      (s) => s.dateObj >= weekStart && s.dateObj <= weekEndLocal,
    );
    if (inWeek) return;

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    let best = dated[0].dateObj;
    let bestDist = Math.abs(best - today);
    dated.forEach((s) => {
      const dist = Math.abs(s.dateObj - today);
      if (dist < bestDist) {
        best = s.dateObj;
        bestDist = dist;
      }
    });
    setWeekStart(startOfWeekMonday(best));
  }, [shifts, loading, weekPinned, weekStart]);

  const weekDays = useMemo(
    () => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)),
    [weekStart],
  );
  const weekEnd = useMemo(() => addDays(weekStart, 6), [weekStart]);

  const weekLabel = useMemo(() => {
    const a = weekStart.toLocaleDateString('en-AU', {
      day: 'numeric',
      month: 'short',
    });
    const b = weekEnd.toLocaleDateString('en-AU', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
    return `${a} – ${b}`;
  }, [weekStart, weekEnd]);

  const managers = useMemo(() => {
    const map = new Map();
    shifts.forEach((s) => {
      if (!s.managerId && !s.manager) return;
      const key = s.managerId || s.manager;
      if (!map.has(key)) map.set(key, s.manager);
    });
    return Array.from(map.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [shifts]);

  const workers = useMemo(() => {
    const map = new Map();
    shifts.forEach((s) => {
      if (s.unassigned) return;
      const key = s.workerId || s.worker;
      if (!key) return;
      if (!map.has(key)) map.set(key, s.worker);
    });
    return Array.from(map.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [shifts]);

  const filteredShifts = useMemo(() => {
    const q = search.trim().toLowerCase();
    return shifts.filter((s) => {
      if (!s.dateObj) return false;
      if (s.dateObj < weekStart || s.dateObj > weekEnd) return false;

      if (managerFilter !== 'all') {
        const key = s.managerId || s.manager;
        if (key !== managerFilter) return false;
      }
      if (workerFilter !== 'all') {
        const key = s.workerId || s.worker;
        if (key !== workerFilter) return false;
      }
      if (typeFilter === 'cleaner' && (s.kind !== 'cleaner' || s.unassigned)) {
        return false;
      }
      if (typeFilter === 'contractor' && s.kind !== 'contractor') return false;
      if (typeFilter === 'unassigned' && !s.unassigned) return false;

      if (!q) return true;
      const hay = `${s.client} ${s.suburb} ${s.worker} ${s.projectName} ${s.taskTitle} ${s.address} ${s.manager} ${s.projectCode}`;
      return hay.toLowerCase().includes(q);
    });
  }, [
    shifts,
    weekStart,
    weekEnd,
    workerFilter,
    managerFilter,
    typeFilter,
    search,
  ]);

  const shiftsByDay = useMemo(() => {
    const map = new Map();
    weekDays.forEach((d) => map.set(toYmd(d), []));
    filteredShifts.forEach((s) => {
      const key = toYmd(s.dateObj);
      if (!map.has(key)) return;
      map.get(key).push(s);
    });
    for (const list of map.values()) {
      list.sort((a, b) => {
        const t = String(a.timeLabel).localeCompare(String(b.timeLabel));
        if (t !== 0) return t;
        const w = String(a.worker).localeCompare(String(b.worker));
        if (w !== 0) return w;
        return String(a.taskTitle).localeCompare(String(b.taskTitle));
      });
    }
    return map;
  }, [filteredShifts, weekDays]);

  const stats = useMemo(() => {
    const scheduled = filteredShifts.length;
    const unassigned = filteredShifts.filter((s) => s.unassigned).length;
    const cleaners = new Set(
      filteredShifts
        .filter((s) => s.kind === 'cleaner' && !s.unassigned)
        .map((s) => s.workerId || s.worker),
    ).size;
    const hours = filteredShifts.reduce((sum, s) => sum + (s.hours || 0), 0);
    return {
      scheduled,
      unassigned,
      cleaners,
      hours: Math.round(hours * 10) / 10,
    };
  }, [filteredShifts]);

  const outsideWeekCount = useMemo(() => {
    return shifts.filter((s) => {
      if (!s.dateObj) return true;
      return s.dateObj < weekStart || s.dateObj > weekEnd;
    }).length;
  }, [shifts, weekStart, weekEnd]);

  const undatedFallbackCount = useMemo(
    () =>
      shifts.filter(
        (s) => s.dateSource === 'project' || s.dateSource === 'assigned',
      ).length,
    [shifts],
  );

  const closeCreateRoster = () => {
    setCreateOpen(false);
    setCreateSearch('');
    setSelectedCreateProject(null);
  };

  const openEditorForProject = (project) => {
    if (!project?.id) return;
    setSelectedCreateProject(project);
    setEditorProjectId(project.id);
    setCreateOpen(false);
  };

  const closeRosterEditor = () => {
    setEditorProjectId(null);
    setSelectedCreateProject(null);
    void load(true);
  };

  const backToCreateProjects = () => {
    setEditorProjectId(null);
    setCreateOpen(true);
  };

  const openCreateRoster = async () => {
    setEditorProjectId(null);
    setSelectedCreateProject(null);
    setCreateOpen(true);
    setCreateSearch('');
    setCreateLoading(true);
    try {
      const res = await graphqlRequest(GET_PROJECTS, { limit: 300 });
      const rows = Array.isArray(res?.findAllProject) ? res.findAllProject : [];
      setCreateProjects(rows);
    } catch (e) {
      console.error(e);
      setCreateProjects([]);
      toast.error(e?.message || 'Failed to load projects');
    } finally {
      setCreateLoading(false);
    }
  };

  const filteredCreateProjects = useMemo(() => {
    const q = createSearch.trim().toLowerCase();
    if (!q) return createProjects;
    return createProjects.filter((p) => {
      const hay = `${p.projectId || ''} ${p.name || ''} ${p.booking?.fullName || ''} ${p.manager?.firstName || ''} ${p.manager?.lastName || ''} ${p.status || ''}`.toLowerCase();
      return hay.includes(q);
    });
  }, [createProjects, createSearch]);

  const goPrevWeek = () => {
    setWeekPinned(true);
    setWeekStart((d) => addDays(d, -7));
  };
  const goNextWeek = () => {
    setWeekPinned(true);
    setWeekStart((d) => addDays(d, 7));
  };
  const goThisWeek = () => {
    setWeekPinned(true);
    setWeekStart(startOfWeekMonday(new Date()));
  };

  const validateWeek = () => {
    if (stats.unassigned > 0) {
      toast.warn(
        `${stats.unassigned} unassigned task(s) this week. Assign cleaners from manager Task Rostering.`,
      );
      return;
    }
    if (stats.scheduled === 0) {
      toast.info('No rostered tasks in this week yet.');
      return;
    }
    toast.success(
      `Week looks complete — ${stats.scheduled} task(s), ${stats.cleaners} cleaner(s).`,
    );
  };

  return (
    <div className="wr-page">
      <header className="wr-header">
        <div>
          <h1>Rosters</h1>
          <p>
            {weekLabel} · Tasks assigned by managers (Task Rostering). Multiple
            tasks for the same cleaner appear as separate cards.
          </p>
        </div>
        <div className="wr-header-actions">
          <button
            type="button"
            className="wr-btn wr-btn--primary"
            onClick={() => void openCreateRoster()}
          >
            <FiPlus /> Create roster
          </button>
          <button
            type="button"
            className="wr-btn"
            onClick={validateWeek}
            disabled={loading}
          >
            <FiCheckCircle /> Validate week
          </button>
          <button
            type="button"
            className="wr-btn"
            onClick={() => void load(true)}
            disabled={loading || refreshing}
          >
            <FiRefreshCw className={refreshing ? 'is-spin' : ''} /> Refresh
          </button>
        </div>
      </header>

      <div className="wr-stats">
        <div className="wr-stat">
          <div className="wr-stat-icon wr-stat-icon--blue">
            <FiBriefcase />
          </div>
          <div>
            <div className="wr-stat-value">{stats.scheduled}</div>
            <div className="wr-stat-label">Scheduled tasks</div>
          </div>
        </div>
        <div className="wr-stat">
          <div className="wr-stat-icon wr-stat-icon--red">
            <FiAlertCircle />
          </div>
          <div>
            <div
              className={`wr-stat-value${stats.unassigned ? ' is-alert' : ''}`}
            >
              {stats.unassigned}
            </div>
            <div className="wr-stat-label">Unassigned</div>
          </div>
        </div>
        <div className="wr-stat">
          <div className="wr-stat-icon wr-stat-icon--green">
            <FiUsers />
          </div>
          <div>
            <div className="wr-stat-value">{stats.cleaners}</div>
            <div className="wr-stat-label">Cleaners on roster</div>
          </div>
        </div>
        <div className="wr-stat">
          <div className="wr-stat-icon wr-stat-icon--purple">
            <FiClock />
          </div>
          <div>
            <div className="wr-stat-value">{stats.hours}</div>
            <div className="wr-stat-label">Scheduled hours</div>
          </div>
        </div>
      </div>

      <div className="wr-toolbar">
        <div className="wr-week-nav">
          <button type="button" className="wr-btn icon" onClick={goPrevWeek}>
            <FiChevronLeft />
          </button>
          <button type="button" className="wr-btn" onClick={goThisWeek}>
            This week
          </button>
          <button type="button" className="wr-btn icon" onClick={goNextWeek}>
            <FiChevronRight />
          </button>
        </div>

        <select
          className="wr-select"
          value={managerFilter}
          onChange={(e) => setManagerFilter(e.target.value)}
        >
          <option value="all">All managers</option>
          {managers.map((m) => (
            <option key={m.id} value={m.id}>
              {m.name}
            </option>
          ))}
        </select>

        <select
          className="wr-select"
          value={workerFilter}
          onChange={(e) => setWorkerFilter(e.target.value)}
        >
          <option value="all">All cleaners / contractors</option>
          {workers.map((w) => (
            <option key={w.id} value={w.id}>
              {w.name}
            </option>
          ))}
        </select>

        <select
          className="wr-select"
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
        >
          <option value="all">All types</option>
          <option value="cleaner">Cleaner tasks</option>
          <option value="contractor">Contractors</option>
          <option value="unassigned">Unassigned only</option>
        </select>

        <div className="wr-search">
          <FiSearch />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search client, project, task, cleaner, address…"
          />
        </div>
      </div>

      {!loading && !err && outsideWeekCount > 0 && filteredShifts.length === 0 ? (
        <div className="wr-banner">
          {outsideWeekCount} assignment
          {outsideWeekCount === 1 ? '' : 's'} sit outside this week
          {undatedFallbackCount
            ? ` (${undatedFallbackCount} using project/assignment date because task date was empty)`
            : ''}
          . Use the week arrows — refresh jumps to the nearest week with work.
        </div>
      ) : null}

      {!loading && !err && undatedFallbackCount > 0 && filteredShifts.length > 0 ? (
        <div className="wr-banner wr-banner--soft">
          {undatedFallbackCount} task
          {undatedFallbackCount === 1 ? '' : 's'} had no task date — shown on
          project start (or assignment day).
        </div>
      ) : null}

      {loading ? (
        <div className="wr-empty">Loading weekly roster…</div>
      ) : err ? (
        <div className="wr-empty">{err}</div>
      ) : (
        <div className="wr-board">
          {weekDays.map((day) => {
            const key = toYmd(day);
            const dayShifts = shiftsByDay.get(key) || [];
            const label = day.toLocaleDateString('en-AU', {
              weekday: 'short',
              day: 'numeric',
            });
            return (
              <section key={key} className="wr-col">
                <div className="wr-col-head">
                  <strong>{label}</strong>
                  <span>
                    {dayShifts.length} task
                    {dayShifts.length === 1 ? '' : 's'}
                  </span>
                </div>
                <div className="wr-col-body">
                  {dayShifts.length === 0 ? (
                    <div className="wr-col-empty">No tasks</div>
                  ) : (
                    dayShifts.map((s) => (
                      <article
                        key={s.id}
                        className={`wr-card${
                          s.unassigned ? ' is-unassigned' : ''
                        }`}
                        style={{ '--accent': s.accent }}
                      >
                        <div className="wr-card-time">
                          {s.timeLabel}
                          {s.suburb ? ` · ${s.suburb}` : ''}
                        </div>
                        <div className="wr-card-site">{s.client}</div>
                        <div className="wr-card-task">{s.taskTitle}</div>
                        {s.projectName ? (
                          <div className="wr-card-project">
                            {s.projectCode ? `${s.projectCode} · ` : ''}
                            {s.projectName}
                          </div>
                        ) : null}
                        <div
                          className={`wr-card-worker${
                            s.unassigned ? ' is-missing' : ''
                          }`}
                        >
                          {s.worker}
                        </div>
                        <div className="wr-card-manager">Mgr: {s.manager}</div>
                        <div className="wr-card-foot">
                          <span
                            className={`wr-role ${
                              s.unassigned
                                ? 'wr-role--unassigned'
                                : s.kind === 'contractor'
                                  ? 'wr-role--contractor'
                                  : 'wr-role--cleaner'
                            }`}
                          >
                            {s.roleLabel}
                          </span>
                        </div>
                      </article>
                    ))
                  )}
                </div>
              </section>
            );
          })}
        </div>
      )}

      {!loading && !err && filteredShifts.length === 0 ? (
        <div className="wr-hint">
          No rostered tasks in this week
          {shifts.length ? ` (${shifts.length} total loaded)` : ''}. Click
          Refresh — the board auto-jumps to the nearest week with assignments.
          Managers assign from employee portal Task Rostering.
        </div>
      ) : null}

      {createOpen
        ? createPortal(
            <div
              className="wr-create-overlay"
              onClick={(event) => {
                if (event.target === event.currentTarget) closeCreateRoster();
              }}
              role="presentation"
            >
              <div
                className="wr-create-modal"
                role="dialog"
                aria-modal="true"
                aria-labelledby="wr-create-title"
                onClick={(event) => event.stopPropagation()}
              >
                <div className="wr-create-head">
                  <div>
                    <h2 id="wr-create-title">Create roster</h2>
                    <p>Pick a project first. Next we’ll add tasks and people.</p>
                  </div>
                  <button
                    type="button"
                    className="wr-btn icon"
                    onClick={closeCreateRoster}
                    aria-label="Close"
                  >
                    <FiX />
                  </button>
                </div>
                <div className="wr-create-search">
                  <FiSearch />
                  <input
                    value={createSearch}
                    onChange={(e) => setCreateSearch(e.target.value)}
                    placeholder="Search project ID, name, client, manager…"
                    autoFocus
                  />
                </div>
                <div className="wr-create-list">
                  {createLoading ? (
                    <div className="wr-empty">Loading projects…</div>
                  ) : filteredCreateProjects.length === 0 ? (
                    <div className="wr-empty">No projects found.</div>
                  ) : (
                    filteredCreateProjects.map((p) => {
                      const managerName = `${p.manager?.firstName || ''} ${
                        p.manager?.lastName || ''
                      }`.trim();
                      const start = p.startDate
                        ? new Date(p.startDate).toLocaleDateString('en-AU', {
                            day: 'numeric',
                            month: 'short',
                            year: 'numeric',
                          })
                        : '';
                      const selected = selectedCreateProject?.id === p.id;
                      return (
                        <button
                          key={p.id}
                          type="button"
                          className={`wr-create-row${
                            selected ? ' is-selected' : ''
                          }`}
                          onClick={() => openEditorForProject(p)}
                        >
                          <span className="wr-create-row-code">
                            {p.projectId || '—'}
                          </span>
                          <span className="wr-create-row-main">
                            <strong>{p.name || 'Untitled project'}</strong>
                            <em>
                              {[p.booking?.fullName, start, p.status]
                                .filter(Boolean)
                                .join(' · ')}
                            </em>
                          </span>
                          {managerName ? (
                            <span className="wr-create-row-manager">
                              Manager: {managerName}
                            </span>
                          ) : null}
                        </button>
                      );
                    })
                  )}
                </div>
                <div className="wr-create-foot">
                  <button
                    type="button"
                    className="wr-btn"
                    onClick={closeCreateRoster}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>,
            document.body,
          )
        : null}

      <RosterTaskEditor
        isOpen={Boolean(editorProjectId)}
        projectId={editorProjectId}
        onBack={backToCreateProjects}
        onClose={closeRosterEditor}
      />
    </div>
  );
}
